// The tablet site identifies a guest parking site by a uid taken from the URL
// (/:uid) or from the redux store. When that value is missing and gets
// concatenated into a path, JavaScript stringifies it: '/' + undefined becomes
// the literal path "/undefined". React Router then matches /:uid with the
// string "undefined", every `uid != null` style guard passes, and the tablet
// calls the API with a uid that cannot resolve. The tablet is left on a dead
// route until someone reloads it by hand.
//
// Use isUsableUid() instead of a null check so those stringified values are
// rejected as well.
export function isUsableUid(value) {
    return typeof value === 'string'
        && value.length > 0
        && value !== 'undefined'
        && value !== 'null';
}

// Each tablet runs in kiosk mode pinned to its own /:uid, so there is no shared
// page we could send a lost tablet back to. Instead every tablet remembers the
// last uid it was legitimately opened with, which lets it put itself back on
// its own site without anyone travelling to it.
//
// Kiosk browsers can run with storage disabled or wiped, so every access is
// guarded and callers must cope with null.
const LAST_UID_KEY = 'guestparking.lastUid';

export function rememberUid(uid) {
    if (!isUsableUid(uid)) {
        return;
    }
    try {
        window.localStorage.setItem(LAST_UID_KEY, uid);
    } catch (e) {
        // Storage unavailable (private mode, quota, locked-down kiosk).
    }
}

export function recallUid() {
    try {
        const uid = window.localStorage.getItem(LAST_UID_KEY);
        return isUsableUid(uid) ? uid : null;
    } catch (e) {
        return null;
    }
}

// Returns the path for a guest parking site, or null when there is no uid worth
// navigating to. Candidates are tried in order, then the remembered uid. A null
// result means "stay put": the app has no route for "/", so navigating there
// renders a blank page.
export function guestSitePath(...candidates) {
    const uid = candidates.find(isUsableUid) || recallUid();
    return uid ? '/' + uid : null;
}
